import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const BACKEND = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const POLL_MS = 5 * 60 * 1000; // 5 min

const TodayContext = createContext(null);

// Fetch all three windows in parallel and merge into a single de-duped list
async function fetchAllWindows() {
  const windows = ['today', 'tomorrow', 'next-7-days'];
  const results = await Promise.allSettled(
    windows.map(win =>
      fetch(`${BACKEND}/api/today?window=${win}`)
        .then(r => r.ok ? r.json() : null)
        .catch(() => null)
    )
  );

  // today = first window — use its count/urgent_count for the badge
  const todayResult = results[0].status === 'fulfilled' ? results[0].value : null;

  // Merge all items, deduplicate by id, sort by due_date then time
  const seen  = new Set();
  const merged = [];
  results.forEach(r => {
    if (r.status !== 'fulfilled' || !r.value?.items) return;
    r.value.items.forEach(item => {
      if (!seen.has(item.id)) {
        seen.add(item.id);
        merged.push(item);
      }
    });
  });
  merged.sort((a, b) => {
    const d = (a.due_date || '').localeCompare(b.due_date || '');
    if (d !== 0) return d;
    return (a.time || '').localeCompare(b.time || '');
  });

  return {
    allItems:     merged,
    count:        todayResult?.count        || 0,
    urgent_count: todayResult?.urgent_count || 0,
  };
}

export function TodayProvider({ children }) {
  const [allItems,    setAllItems]    = useState([]);
  const [count,       setCount]       = useState(0);
  const [urgentCount, setUrgentCount] = useState(0);
  const [loading,     setLoading]     = useState(false);
  const [lastFetched, setLastFetched] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAllWindows();
      setAllItems(data.allItems);
      setCount(data.count);
      setUrgentCount(data.urgent_count);
      setLastFetched(new Date());
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  // On mount + every 5 min
  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  // Toggle checked/unchecked — reversible. MACRO only in H2; no-op for other sources.
  const toggleDone = useCallback(async (itemId) => {
    try {
      await fetch(`${BACKEND}/api/today/${itemId}/done`, { method: 'POST' });
      await refresh();
    } catch (e) {
      // noop
    }
  }, [refresh]);

  return (
    <TodayContext.Provider value={{
      allItems, count, urgentCount,
      refresh, toggleDone, loading, lastFetched,
    }}>
      {children}
    </TodayContext.Provider>
  );
}

export function useToday() {
  const ctx = useContext(TodayContext);
  if (!ctx) throw new Error('useToday must be used inside TodayProvider');
  return ctx;
}
